import React, { useState } from 'react';
import './MainMenu.css';

const DIFFICULTIES = [
  { key: 'easy', label: 'Einfach', desc: '1-stellig' },
  { key: 'medium', label: 'Mittel', desc: '2-stellig' },
  { key: 'hard', label: 'Schwer', desc: '3-stellig' },
];

const DIFFICULTIES_FRACTIONS = [
  { key: 'easy', label: 'Einfach', desc: 'Einfache Brüche' },
  { key: 'medium', label: 'Mittel', desc: 'Gemischte Brüche' },
  { key: 'hard', label: 'Schwer', desc: '2-stellig' },
];

const OPERATIONS_NATURAL = [
  { key: 'add', label: 'Plus', symbol: '+' },
  { key: 'subtract', label: 'Minus', symbol: '−' },
  { key: 'multiply', label: 'Mal', symbol: '×' },
  { key: 'divide', label: 'Geteilt', symbol: '÷' },
];

const OPERATIONS_FRACTIONS = [
  { key: 'add', label: 'Plus', symbol: '+' },
  { key: 'subtract', label: 'Minus', symbol: '−' },
  { key: 'multiply', label: 'Mal', symbol: '×' },
  { key: 'divide', label: 'Geteilt', symbol: '÷' },
  { key: 'expand', label: 'Erweitern', symbol: '↗' },
  { key: 'reduce', label: 'Kürzen', symbol: '↘' },
];

const DURATIONS = [3, 6, 9];

export default function MainMenu({ onStart }) {
  const [difficulty, setDifficulty] = useState('easy');
  const [operations, setOperations] = useState(['add']);
  const [numberType, setNumberType] = useState('natural');
  const [duration, setDuration] = useState(3);
  const [arcadeMode, setArcadeMode] = useState(false);

  const availableOps = numberType === 'fractions' ? OPERATIONS_FRACTIONS : OPERATIONS_NATURAL;

  const toggleOperation = (key) => {
    setOperations(prev => {
      if (prev.includes(key)) {
        // Don't allow deselecting the last one
        if (prev.length === 1) return prev;
        return prev.filter(k => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleNumberTypeChange = (type) => {
    setNumberType(type);
    if (type === 'natural') {
      // Remove fraction-only operations
      setOperations(prev => {
        const filtered = prev.filter(k => k !== 'expand' && k !== 'reduce');
        return filtered.length > 0 ? filtered : ['add'];
      });
    }
  };

  const handleStart = () => {
    onStart({ difficulty, operations, numberType, duration: arcadeMode ? 3 : duration, arcadeMode });
  };

  return (
    <div className="menu-container">
      <div className="menu-header">
        <div className="logo-glow" />
        <h1 className="menu-title">
          <span className="title-cobra">Cobra</span>
          <span className="title-calc">Calc</span>
        </h1>
        <p className="menu-subtitle">Mathe-Trainer</p>
      </div>

      <div className="menu-sections">
        {/* Number Type */}
        <div className="menu-section">
          <h3 className="section-label">Zahlenart</h3>
          <div className="option-row">
            <button
              className={`option-btn type-btn ${numberType === 'natural' ? 'active' : ''}`}
              onClick={() => handleNumberTypeChange('natural')}
            >
              <span className="btn-icon">123</span>
              <span className="btn-label">Natürliche Zahlen</span>
            </button>
            <button
              className={`option-btn type-btn ${numberType === 'fractions' ? 'active' : ''}`}
              onClick={() => handleNumberTypeChange('fractions')}
            >
              <span className="btn-icon fraction-icon">
                <span>a</span>
                <span className="frac-line" />
                <span>b</span>
              </span>
              <span className="btn-label">Brüche</span>
            </button>
          </div>
        </div>

        {/* Difficulty */}
        <div className="menu-section">
          <h3 className="section-label">Schwierigkeit</h3>
          <div className="option-row triple">
            {(numberType === 'fractions' ? DIFFICULTIES_FRACTIONS : DIFFICULTIES).map(d => (
              <button
                key={d.key}
                className={`option-btn diff-btn ${difficulty === d.key ? 'active' : ''}`}
                onClick={() => setDifficulty(d.key)}
              >
                <span className="btn-label">{d.label}</span>
                <span className="btn-desc">{d.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Operation - Multi-select */}
        <div className="menu-section">
          <h3 className="section-label">Rechenarten <span className="section-hint">(Mehrfachauswahl)</span></h3>
          <div className="option-row ops">
            {availableOps.map(op => (
              <button
                key={op.key}
                className={`option-btn op-btn ${operations.includes(op.key) ? 'active' : ''}`}
                onClick={() => toggleOperation(op.key)}
              >
                <span className="op-symbol">{op.symbol}</span>
                <span className="btn-label">{op.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Duration */}
        <div className={`menu-section ${arcadeMode ? 'section-disabled' : ''}`}>
          <h3 className="section-label">
            Rundendauer
            {arcadeMode && <span className="section-hint"> (Arcade: 3 Min. fest)</span>}
          </h3>
          <div className="option-row triple">
            {DURATIONS.map(d => (
              <button
                key={d}
                className={`option-btn dur-btn ${(arcadeMode ? 3 : duration) === d ? 'active' : ''}`}
                onClick={() => !arcadeMode && setDuration(d)}
                disabled={arcadeMode}
              >
                <span className="dur-number">{d}</span>
                <span className="btn-label">Minuten</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Arcade Mode */}
      <div className="menu-section arcade-section">
        <button
          className={`arcade-toggle ${arcadeMode ? 'active' : ''}`}
          onClick={() => setArcadeMode(prev => !prev)}
        >
          <span className="arcade-icon">{arcadeMode ? '🕹' : '🎯'}</span>
          <div className="arcade-text">
            <span className="arcade-label">Arcade Mode</span>
            <span className="arcade-desc">{arcadeMode ? 'Crosshair aktiv · Sounds an' : 'Klicken zum Aktivieren'}</span>
          </div>
          <span className={`arcade-switch ${arcadeMode ? 'on' : ''}`}>
            <span className="arcade-switch-knob" />
          </span>
        </button>
      </div>

      <button className="start-btn" onClick={handleStart}>
        <span className="start-text">Starten</span>
        <span className="start-arrow">→</span>
      </button>
    </div>
  );
}
